import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import TransitionShell from './common/TransitionShell';
import { traceRouting } from '../utils/routingDebug';

// D23 — internal domains never require billing
const INTERNAL_DOMAINS = ['people1.ai', 'flock.io', 'aiflock.ai'];

function isInternalUser(email) {
  if (!email) return false;
  const domain = email.split('@')[1] ?? '';
  if (INTERNAL_DOMAINS.includes(domain)) return true;
  const extra = (process.env.REACT_APP_INTERNAL_USER_EMAILS ?? '').split(',').map(s => s.trim()).filter(Boolean);
  return extra.includes(email);
}

// Returns true if a subscription row from /api/billing/subscription is
// currently usable (active, in grace, or canceled-with-period-remaining).
// dev_internal (Flock team) always passes; code_redeemed (external code clients) too, unless it
// is a comp the aggregator already cut at the end of its period (#759): past_due with its grace
// already over, the same shape as an expired trial.
function isSubActive(sub) {
  if (!sub) return false;
  if (sub.source === 'dev_internal') return true;
  const now = new Date();
  if (sub.status === 'past_due' && sub.grace_until && new Date(sub.grace_until) < now) return false;
  if (sub.source === 'code_redeemed') return true;
  if (sub.status === 'canceled' && sub.current_period_end && new Date(sub.current_period_end) < now) return false;
  return true;
}

/**
 * OnboardingGuard - Bidirectional onboarding routing guard
 *
 * Redirects synchronously (no flash) using <Navigate> in the render tree.
 *
 * Four layers (evaluated in order):
 * 0. TERMS GATE — `terms_accepted_at` IS NULL forces /onboarding/terms on
 *    every authenticated route, including other /onboarding/* steps.
 * 1. BILLING GATE (F7) — if org has no active subscription and user is not
 *    dev_internal or code_redeemed (D23), redirect to /billing/required.
 * 2. FORWARD (non-onboarding routes): redirect TO the next pending onboarding step.
 * 3. REVERSE (onboarding routes): redirect AWAY when the user already passed that step.
 *
 * @param {ReactNode} children - Components to render if routing is correct
 */
const OnboardingGuard = ({ children }) => {
  const { userProfile, loading } = useAuth();
  const location = useLocation();
  // 'loading'  → fetch en vuelo
  // 'skipped'  → no se ha consultado (T&C aún sin aceptar; el backend daría 451)
  // 'ok' | 'required' → veredicto real de /api/billing/subscription
  //
  // #1185: 'skipped' NO puede compartir valor con 'ok'. Cuando lo compartían, el
  // instante entre aceptar T&C y recibir el veredicto se leía como "tiene
  // suscripción" y el guard pintaba la pantalla de destino (ConnectAppsPage)
  // antes de mandar a /billing/required. "No lo he mirado" y "está bien" son
  // estados distintos.
  const [subStatus, setSubStatus] = useState('loading');

  useEffect(() => {
    if (!userProfile) return;

    const params = new URLSearchParams(location.search);
    const isPostCheckout = params.get('billing') === 'success';

    // Bypass billing check for: internal users, /billing/* routes.
    // NOTE: /onboarding/terms is NOT bypassed — we need the result to decide
    // where to route after the user accepts terms (billing vs onboarding).
    // NOTE: ?billing=success used to bypass too — now we fetch with polling
    // (3× 1s) to handle the race where Stripe redirects before the webhook
    // has persisted the subscription. If polling fails, fall through to
    // 'required' so we don't leave the user in a broken state.
    if (
      isInternalUser(userProfile.email) ||
      location.pathname.startsWith('/billing')
    ) {
      setSubStatus('ok');
      return;
    }

    // Skip billing fetch if user has not accepted T&C yet: the backend returns
    // 451 on /api/billing/subscription (requireTermsAccepted middleware), which
    // apiClient handles by force-redirecting to /onboarding/terms — interrupting
    // any other in-flight fetches (e.g. AuthContext useRegistrationCode). The
    // render path below already redirects to /onboarding/terms (line ~146), so
    // the user gets there cleanly without the 451-driven imperative redirect.
    if (!userProfile.terms_accepted_at) {
      setSubStatus('skipped'); // sentinel: gates render-time terms check takes precedence
      return;
    }

    let cancelled = false;
    // Después de pagar, el alta llega por webhook: ~10 s antes de dar por hecho que algo ha ido
    // mal y mandar al aviso de activación de /billing/required.
    const MAX_ATTEMPTS = isPostCheckout ? 10 : 1;
    const DELAY_MS = 1000;

    const fetchOnce = (attempt = 1) => {
      apiClient.get('/api/billing/subscription')
        .then(sub => {
          // apiClient.request() already unwraps {success, data} → sub is the row or null
          if (cancelled) return;
          if (isSubActive(sub)) {
            setSubStatus('ok');
            return;
          }
          if (attempt < MAX_ATTEMPTS) {
            setTimeout(() => { if (!cancelled) fetchOnce(attempt + 1); }, DELAY_MS);
          } else {
            // #759 R: quien paga por Stripe y tiene la renovación sin cobrar no necesita otro plan:
            // necesita pagar la que tiene.
            setSubStatus(sub?.source === 'stripe' && sub?.status === 'past_due' ? 'renewal' : 'required');
          }
        })
        .catch(() => {
          if (cancelled) return;
          // Decisión: fail-open en caso de error de red. Si el backend de billing no
          // responde (downtime, deploy en curso), preferimos dejar entrar al user a
          // la app antes que mostrarle /billing/required falsamente. La invariante de
          // que un user con sub válida puede entrar se mantiene; el riesgo es dejar
          // entrar a un user sin sub activa durante un outage, lo cual es preferible
          // a bloquear a users válidos. Revisar si pasamos a fail-closed cuando
          // tengamos circuit breaker + health endpoint diferenciado.
          setSubStatus('ok');
        });
    };

    fetchOnce();

    return () => { cancelled = true; };
  }, [userProfile, location.pathname, location.search]);

  // PrivateRoute shows spinner when loading=true. But when loading=false and
  // userProfile=null (race: user set by onAuthStateChange before loadUserProfile
  // resolves — e.g. PKCE email confirmation), PrivateRoute renders children with
  // no spinner. Show TransitionShell to avoid a blank screen.
  if (loading) {
    traceRouting('OnboardingGuard', 'TransitionShell', { reason: 'auth_loading', path: location.pathname });
    return <TransitionShell label="Cargando..." />;
  }
  if (!userProfile) {
    traceRouting('OnboardingGuard', 'TransitionShell', { reason: 'profile_loading', path: location.pathname });
    return <TransitionShell label="Verificando cuenta..." />;
  }

  // ── Step 0: Terms gate ────────────────────────────────────────────
  if (!userProfile.terms_accepted_at && location.pathname !== '/onboarding/terms') {
    traceRouting('OnboardingGuard', 'Navigate', { to: '/onboarding/terms', reason: 'terms_null', path: location.pathname });
    return <Navigate to="/onboarding/terms" replace />;
  }

  // ── Step 1: Billing gate (F7) ─────────────────────────────────────
  // Fires on ALL routes except /billing/* and /onboarding/terms (payment comes before onboarding)
  const isBillingRoute = location.pathname.startsWith('/billing');
  const isOnboardingRoute = location.pathname.startsWith('/onboarding/');
  const isTermsRoute = location.pathname === '/onboarding/terms';
  // Sin veredicto todavía. 'skipped' solo sobrevive al render cuando el usuario
  // acaba de aceptar los T&C: el effect ya ha vuelto a lanzar el fetch, pero el
  // render ocurre antes de que responda. Tratarlo como 'loading' es lo que evita
  // enseñar la pantalla equivocada durante ese hueco (#1185).
  const subPending = subStatus === 'loading' || subStatus === 'skipped';

  if (!isBillingRoute && !isTermsRoute && subPending) {
    const params = new URLSearchParams(location.search);
    if (params.get('billing') === 'success') {
      traceRouting('OnboardingGuard', 'TransitionShell', { reason: 'post_checkout_polling', path: location.pathname });
      return <TransitionShell label="Activando tu plan…" sublabel="Esto solo tarda un par de segundos." />;
    }
    traceRouting('OnboardingGuard', 'TransitionShell', { reason: 'subStatus_loading', path: location.pathname });
    return <TransitionShell label="Verificando suscripción..." />;
  }
  if (!isBillingRoute && !isTermsRoute && subStatus === 'renewal') {
    traceRouting('OnboardingGuard', 'Navigate', { to: '/billing/renewal', reason: 'renewal_unpaid', path: location.pathname });
    return <Navigate to="/billing/renewal" replace />;
  }
  if (!isBillingRoute && !isTermsRoute && subStatus === 'required') {
    // Vuelve de pagar y la suscripción no aparece: el alta ha fallado o va lenta. La página de
    // planes enseña entonces el aviso en vez de ofrecer pagar otra vez.
    const to = new URLSearchParams(location.search).get('billing') === 'success'
      ? '/billing/required?activation=pending'
      : '/billing/required';
    traceRouting('OnboardingGuard', 'Navigate', { to, reason: 'subStatus_required', path: location.pathname });
    return <Navigate to={to} replace />;
  }

  if (isOnboardingRoute) {
    // ── Reverse guard: redirect AWAY from onboarding ──────────────────
    if (location.pathname === '/onboarding/terms' && userProfile.terms_accepted_at) {
      // Wait for billing check before routing away from terms
      if (!isInternalUser(userProfile.email) && subPending) {
        traceRouting('OnboardingGuard', 'TransitionShell', { reason: 'terms_done_subStatus_loading', path: location.pathname });
        return <TransitionShell label="Continuando configuración..." />;
      }
      if (!isInternalUser(userProfile.email) && (subStatus === 'required' || subStatus === 'renewal')) {
        const to = subStatus === 'renewal' ? '/billing/renewal' : '/billing/required';
        traceRouting('OnboardingGuard', 'Navigate', { to, reason: 'terms_done_no_sub', path: location.pathname });
        return <Navigate to={to} replace />;
      }
      const next = userProfile.has_logged_in === false
        ? '/onboarding/connect'
        : userProfile.onboarding_completed ? '/' : '/onboarding/chat';
      traceRouting('OnboardingGuard', 'Navigate', { to: next, reason: 'terms_done_sub_ok', path: location.pathname });
      return <Navigate to={next} replace />;
    }
    if (location.pathname === '/onboarding/connect' && userProfile.has_logged_in !== false) {
      const target = userProfile.onboarding_completed ? '/' : '/onboarding/chat';
      traceRouting('OnboardingGuard', 'Navigate', { to: target, reason: 'connect_already_logged_in', path: location.pathname });
      return <Navigate to={target} replace />;
    }
    if (location.pathname.startsWith('/onboarding/chat') && userProfile.onboarding_completed && !userProfile.onboarding_skipped_at) {
      traceRouting('OnboardingGuard', 'Navigate', { to: '/', reason: 'chat_already_completed', path: location.pathname });
      return <Navigate to="/" replace />;
    }
  } else if (!isBillingRoute) {
    // ── Forward guard: redirect TO onboarding ─────────────────────────
    if (userProfile.has_logged_in === false) {
      traceRouting('OnboardingGuard', 'Navigate', { to: '/onboarding/connect', reason: 'has_logged_in_false', path: location.pathname });
      return <Navigate to="/onboarding/connect" replace />;
    }
    if (userProfile.onboarding_completed === false) {
      traceRouting('OnboardingGuard', 'Navigate', { to: '/onboarding/chat', reason: 'onboarding_not_completed', path: location.pathname });
      return <Navigate to="/onboarding/chat" replace />;
    }
    // Con `?billing=success` MainLayout pinta sin barra lateral mientras se sondea. Si nada más
    // redirige —quien ya hizo el onboarding y paga o canjea un código—, hay que quitarlo o se
    // queda en el chat sin menú.
    const params = new URLSearchParams(location.search);
    if (params.get('billing') === 'success') {
      params.delete('billing');
      const search = params.toString();
      traceRouting('OnboardingGuard', 'Navigate', { to: location.pathname, reason: 'post_checkout_done', path: location.pathname });
      return <Navigate to={{ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }} replace />;
    }
  }

  return children;
};

export default OnboardingGuard;
